import { useState, useCallback } from 'react';
import { Player } from '../types';

interface DragAndDropState {
  isDragging: boolean;
  draggedPlayer: Player | null;
  dragOverPlayer: Player | null;
  showDragPreview: boolean;
}

interface DragAndDropActions {
  handleDragStart: (player: Player) => void;
  handleDragEnd: () => void;
  handleDragOver: (player: Player | null) => void;
  handleDrop: (
    targetPlayer: Player | null,
    targetTeamIndex?: number,
    targetPositionIndex?: number
  ) => Promise<void>;
}

interface UseDragAndDropProps {
  userIsCreator: boolean;
  onShowNotification: (message: string, type: 'success' | 'error') => void;
  onSwapPlayers: (player1Id: string, player2Id: string) => Promise<void>;
  onMovePlayer: (playerId: string, teamIndex: number, positionIndex: number) => Promise<void>;
  onSyncTeams: () => Promise<void>;
}

export const useDragAndDrop = ({
  userIsCreator,
  onShowNotification,
  onSwapPlayers,
  onMovePlayer,
  onSyncTeams
}: UseDragAndDropProps): DragAndDropState & DragAndDropActions => {
  const [isDragging, setIsDragging] = useState(false);
  const [draggedPlayer, setDraggedPlayer] = useState<Player | null>(null);
  const [dragOverPlayer, setDragOverPlayer] = useState<Player | null>(null);
  const [showDragPreview, setShowDragPreview] = useState(false);

  const handleDragStart = useCallback((player: Player) => {
    if (!userIsCreator) return;
    
    setIsDragging(true);
    setDraggedPlayer(player);
    setShowDragPreview(true);
  }, [userIsCreator]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDraggedPlayer(null);
    setDragOverPlayer(null);
    setShowDragPreview(false);
  }, []);

  const handleDragOver = useCallback((player: Player | null) => {
    if (!draggedPlayer || draggedPlayer.id === player?.id) return;
    setDragOverPlayer(player);
  }, [draggedPlayer]);

  const handleDrop = useCallback(async (
    targetPlayer: Player | null,
    targetTeamIndex?: number,
    targetPositionIndex?: number
  ) => {
    if (!draggedPlayer || !userIsCreator) {
      handleDragEnd();
      return;
    }

    if (draggedPlayer.id === targetPlayer?.id) {
      handleDragEnd();
      return;
    }

    // Verificar si el intercambio es válido
    const draggedIsSubstitute = draggedPlayer.isSubstitute;
    const targetIsSubstitute = targetPlayer?.isSubstitute;
    
    // Si ambos son suplentes, no permitir intercambio
    if (draggedIsSubstitute && targetIsSubstitute) {
      onShowNotification('No se pueden intercambiar suplentes entre sí', 'error');
      handleDragEnd();
      return;
    }

    try {
      // Si el objetivo es un cupo vacío (null) y tenemos información de posición
      if (!targetPlayer && targetTeamIndex !== undefined && targetPositionIndex !== undefined) {
        if (draggedIsSubstitute) {
          // Promover suplente a jugador principal en posición específica
          await onMovePlayer(draggedPlayer.id, targetTeamIndex, targetPositionIndex);
          onShowNotification(`${draggedPlayer.name} promovido a jugador principal en posición ${targetPositionIndex + 1}`, 'success');
        } else {
          // Mover jugador titular a posición específica
          await onMovePlayer(draggedPlayer.id, targetTeamIndex, targetPositionIndex);
          onShowNotification(`${draggedPlayer.name} movido a posición ${targetPositionIndex + 1}`, 'success');
        }
        
        await onSyncTeams();
        handleDragEnd();
        return;
      }

      // Si el objetivo es un cupo vacío pero sin información de posición (área de suplentes)
      if (!targetPlayer && targetTeamIndex === undefined && targetPositionIndex === undefined) {
        if (draggedIsSubstitute) {
          // Promover suplente a jugador principal (sin posición específica)
          await onSwapPlayers(draggedPlayer.id, draggedPlayer.id);
          onShowNotification(`${draggedPlayer.name} promovido a jugador principal`, 'success');
        } else {
          // Titular movido al área de suplentes
          await onSwapPlayers(draggedPlayer.id, draggedPlayer.id);
          onShowNotification(`${draggedPlayer.name} movido a suplentes`, 'success');
        }
        
        await onSyncTeams();
        handleDragEnd();
        return;
      }

      // Intercambio normal entre jugadores
      if (!targetPlayer) {
        handleDragEnd();
        return;
      }
      
      await onSwapPlayers(draggedPlayer.id, targetPlayer.id);

      // Mostrar mensaje específico según el tipo de intercambio
      if (draggedIsSubstitute && !targetIsSubstitute) {
        onShowNotification(`${draggedPlayer.name} promovido a jugador principal, ${targetPlayer.name} movido a suplentes`, 'success');
      } else if (!draggedIsSubstitute && targetIsSubstitute) {
        onShowNotification(`${draggedPlayer.name} movido a suplentes, ${targetPlayer.name} promovido a jugador principal`, 'success');
      } else {
        onShowNotification('Jugadores intercambiados exitosamente', 'success');
      }
      
      await onSyncTeams();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in drag and drop operation:', error);
      onShowNotification('Error en la operación', 'error');
    } finally {
      handleDragEnd();
    }
  }, [
    draggedPlayer,
    userIsCreator,
    onShowNotification,
    onSwapPlayers,
    onMovePlayer,
    onSyncTeams,
    handleDragEnd
  ]);

  return {
    // State
    isDragging,
    draggedPlayer,
    dragOverPlayer,
    showDragPreview,
    
    // Actions
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop
  };
};
